import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Stack,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import RichTextEditor from "../common/RichTextEditor";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { createBlog, updateBlog, getBlogDetails } from "../apis/Blog";
import { toast } from "react-toastify";

const BlogForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const [loading, setLoading] = useState(isEditing);

  const [blog, setBlog] = useState({
    title: "",
    description: "",
    status: "Draft",
    files: [],
    faqs: [],
  });

  const [errors, setErrors] = useState({
    title: false,
    description: false,
  });

  const [selectedImages, setSelectedImages] = useState([]);

  useEffect(() => {
    const fetchBlogDetails = async () => {
      if (isEditing) {
        try {
          setLoading(true);
          const response = await getBlogDetails(id);

          // Convert files to the format expected by the form
          const formattedFiles =
            response.files?.map((file) => ({
              file: null, // We don't have the actual file object
              preview: file.url,
              name: file.name,
            })) || [];

          setBlog({
            title: response.title,
            description: response.description,
            status: response.status,
            files: response.files || [],
            faqs: response.faqs || [],
          });
          setSelectedImages(formattedFiles);
        } catch (error) {
          toast.error("Failed to fetch blog details");
          console.error("Error fetching blog details:", error);
          navigate("/blogs");
        } finally {
          setLoading(false);
        }
      }
    };

    fetchBlogDetails();
  }, [id, isEditing, navigate]);

  const validateForm = () => {
    const newErrors = {
      title: !blog.title.trim(),
      description: !blog.description.trim(),
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error);
  };

  const handleImageChange = (event) => {
    const files = Array.from(event.target.files);
    const newImages = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setSelectedImages((prev) => [...prev, ...newImages]);
  };

  const removeImage = (index) => {
    setSelectedImages((prev) => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  const handleFaqChange = (index, field, value) => {
    const updatedFaqs = [...blog.faqs];
    updatedFaqs[index] = {
      ...updatedFaqs[index],
      [field]: value,
    };
    setBlog((prev) => ({
      ...prev,
      faqs: updatedFaqs,
    }));
  };

  const addFaq = () => {
    setBlog((prev) => ({
      ...prev,
      faqs: [...prev.faqs, { question: "", answer: "" }],
    }));
  };

  const removeFaq = (index) => {
    setBlog((prev) => ({
      ...prev,
      faqs: prev.faqs.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async (publish = false) => {
    if (!validateForm()) {
      return;
    }

    try {
      const formData = new FormData();
      formData.append("title", blog.title);
      formData.append("description", blog.description);
      formData.append("status", publish ? "Published" : blog.status);

      // Filter out FAQs with empty questions or answers
      const validFaqs = blog.faqs.filter(
        (faq) => faq.question.trim() !== "" && faq.answer.trim() !== ""
      );
      formData.append("faqs", JSON.stringify(validFaqs));

      // Handle files - append each file to FormData
      selectedImages.forEach((image) => {
        if (image.file) {
          formData.append("files", image.file);
        }
      });

      if (isEditing) {
        await updateBlog(id, formData);
      } else {
        await createBlog(formData);
      }
      toast.success("Blog saved successfully");
      navigate("/blogs");
    } catch (error) {
      toast.error("Error saving blog");
      console.error("Error saving blog:", error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" component="h1" sx={{ mb: 4 }}>
          {isEditing ? "Edit Blog" : "Create New Blog"}
        </Typography>

        <Stack spacing={3}>
          <TextField
            label="Title"
            fullWidth
            required
            error={errors.title}
            helperText={errors.title ? "Title is required" : ""}
            value={blog.title}
            onChange={(e) => {
              setBlog({ ...blog, title: e.target.value });
              if (errors.title) {
                setErrors((prev) => ({ ...prev, title: false }));
              }
            }}
          />

          <Box>
            <Typography
              variant="subtitle2"
              color={errors.description ? "error" : "text.secondary"}
              sx={{ mb: 1 }}
            >
              Description* {errors.description && "- Required"}
            </Typography>
            <RichTextEditor
              value={blog.description}
              onChange={(value) => {
                console.log({ description: value });
                setBlog({ ...blog, description: value });
                if (errors.description) {
                  setErrors((prev) => ({ ...prev, description: false }));
                }
              }}
              placeholder="Start writing your blog post..."
            />
          </Box>

          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Images
            </Typography>
            <input
              accept="image/*"
              style={{ display: "none" }}
              id="image-upload"
              type="file"
              multiple
              onChange={handleImageChange}
            />
            <label htmlFor="image-upload">
              <Button variant="outlined" component="span">
                Upload Images
              </Button>
            </label>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 2 }}>
              {selectedImages.map((image, index) => (
                <Box
                  key={index}
                  sx={{
                    position: "relative",
                    width: 150,
                    height: 150,
                  }}
                >
                  <img
                    src={image.preview}
                    alt={`Preview ${index + 1}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: 4,
                    }}
                  />
                  <IconButton
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      bgcolor: "rgba(255, 255, 255, 0.8)",
                      "&:hover": {
                        bgcolor: "rgba(255, 255, 255, 0.9)",
                      },
                    }}
                    onClick={() => removeImage(index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Box>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Typography variant="subtitle1">FAQs</Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={addFaq}
                variant="outlined"
                size="small"
              >
                Add FAQ
              </Button>
            </Box>

            {blog.faqs.map((faq, index) => (
              <Box
                key={index}
                sx={{
                  mb: 3,
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}
                >
                  <IconButton
                    size="small"
                    onClick={() => removeFaq(index)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
                <Stack spacing={2}>
                  <TextField
                    label="Question"
                    fullWidth
                    value={faq.question}
                    onChange={(e) =>
                      handleFaqChange(index, "question", e.target.value)
                    }
                    placeholder="Enter your question"
                  />
                  <TextField
                    label="Answer"
                    fullWidth
                    multiline
                    rows={3}
                    value={faq.answer}
                    onChange={(e) =>
                      handleFaqChange(index, "answer", e.target.value)
                    }
                    placeholder="Enter your answer"
                  />
                </Stack>
              </Box>
            ))}
          </Box>

          <Box
            sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 4 }}
          >
            <Button variant="outlined" onClick={() => navigate("/blogs")}>
              Cancel
            </Button>
            <Button
              onClick={() => handleSave(false)}
              variant="outlined"
              color="primary"
            >
              Save as Draft
            </Button>
            <Button
              onClick={() => handleSave(true)}
              variant="contained"
              color="primary"
            >
              {isEditing ? "Update & Publish" : "Save & Publish"}
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default BlogForm;
